import { InfoIcon } from '@phosphor-icons/react';

import { formatCount, formatTokens, formatUsd, type TeamUsage } from '@/api-models/usage';
import { Card } from '@/ui/Card';

/**
 * The headline: what the window cost, and the handful of figures that qualify it.
 *
 * The hero number is the whole point of the page, so it leads and nothing else
 * competes with it. The caveats underneath — unpriced calls, applied corrections —
 * are rendered only when they apply, because a spend figure that is quietly a
 * lower bound is worse than one that says so.
 */
export function UsageSummary({ usage }: { usage: TeamUsage }) {
  const unpriced = usage.unpriced_request_count + usage.unpriced_tool_request_count;
  const totalTokens = usage.prompt_tokens + usage.completion_tokens;
  const cacheRate = usage.prompt_tokens > 0 ? usage.cached_prompt_tokens / usage.prompt_tokens : 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <p className="text-xs font-medium text-fg-tertiary">Total spend</p>
        <p className="text-[44px] font-semibold leading-none tracking-tight text-fg">
          {formatUsd(usage.total_micro_usd)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Tile
          label="Model calls"
          value={formatUsd(usage.llm_micro_usd)}
          hint={`${formatCount(usage.request_count)} requests`}
        />
        <Tile
          label="Tools & search"
          value={formatUsd(usage.tool_micro_usd)}
          hint={`${formatCount(usage.tool_request_count)} calls`}
        />
        <Tile
          label="Tokens"
          value={formatTokens(totalTokens)}
          hint={`${Math.round(cacheRate * 100)}% of input cached`}
        />
        <Tile
          label="Sessions"
          value={formatCount(usage.session_count)}
          hint={`${formatCount(usage.active_user_count)} ${
            usage.active_user_count === 1 ? 'member' : 'members'
          } active`}
        />
      </div>

      {(unpriced > 0 || usage.correction_count > 0) && (
        <div className="flex flex-col gap-1.5">
          {unpriced > 0 && (
            <Caveat>
              {formatCount(unpriced)} {unpriced === 1 ? 'call was' : 'calls were'} billed as zero because the provider
              reported no usage — the total above is a lower bound.
            </Caveat>
          )}
          {usage.correction_count > 0 && (
            <Caveat>
              {formatCount(usage.correction_count)}{' '}
              {usage.correction_count === 1 ? 'billing correction is' : 'billing corrections are'} applied, netting{' '}
              {formatUsd(usage.corrections_micro_usd)}.
            </Caveat>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card radius="lg" padding="sm" className="flex flex-col gap-1.5">
      <span className="text-xs text-fg-tertiary">{label}</span>
      <span className="text-2xl font-semibold leading-none text-fg">{value}</span>
      <span className="text-xs text-fg-muted">{hint}</span>
    </Card>
  );
}

function Caveat({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-1.5 text-xs leading-4.5 text-fg-tertiary">
      <InfoIcon size={13} weight="duotone" className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
